import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { posthog } from 'posthog-js';
import { apiEnums, ApiModel, useApiClient } from '@nodal/api';
import { TopNav } from 'components/TopNav';
import { Screening } from 'components/Screening';
import { Matching } from 'components/Matching';
import { ContactDetails } from 'components/ContactDetails';
import { SubscriptionFlow } from 'components/SubscriptionFlow';
import { AppProtectedContextValue } from './App.interface';
import { MatchingInProgress } from 'components/Matching/MatchingProgress';
import { useForcedRedirect } from './AppAccess';
import { settings } from 'settings';
import { useUsersMeRetrieve } from 'api/hooks/useUsersMeRetrieve';
import { AppShell } from '@nodal/uikit/components/AppShell';
import { LoadingScreen } from '@nodal/uikit/components/LoadingScreen';
import { ModalProvider } from '@nodal/uikit/components/Modal';
import { useQuery } from 'react-query';
import { queryKeys } from '@nodal/core/consts/query';
import { appPaths, paths } from 'consts/paths';
import { SinglePaymentService } from 'components/SinglePaymentService';
import { User } from 'api';
import { UploadRecords } from 'components/UploadRecords';
import { CareGapRecommendations } from 'components/CareGapRecommendations';
import { ConfirmationDialogProvider } from '@nodal/uikit/components/ConfirmationDialog';
import { Navigator } from 'components/Navigator';
import { Dashboard } from 'components/Dashboard';

const PaymentServiceElement: FC<{
  role: User['role'];
  status: ApiModel.UserStatusEnum;
  isSubscription: boolean;
  isMatchProfileCompleted?: boolean;
  matchProfileReviewStatus?: ApiModel.DonorProfile['match_profile_review_status'];
}> = ({
  role,
  status,
  isSubscription,
  isMatchProfileCompleted,
  matchProfileReviewStatus,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (status === apiEnums.UserStatusEnum['Scr+']) {
      if (role === apiEnums.UserRoleEnum.Nap) {
        navigate(paths.app);
      } else if (
        role === apiEnums.UserRoleEnum.Par &&
        (!isMatchProfileCompleted ||
          matchProfileReviewStatus ===
            apiEnums.MatchProfileReviewStatusEnum.ToReview)
      ) {
        navigate(appPaths.matching);
      }
    }
  }, [
    navigate,
    role,
    status,
    isMatchProfileCompleted,
    matchProfileReviewStatus,
  ]);

  if (
    status === apiEnums.UserStatusEnum['Scr+'] &&
    role === apiEnums.UserRoleEnum.Par &&
    isMatchProfileCompleted &&
    matchProfileReviewStatus !== apiEnums.MatchProfileReviewStatusEnum.ToReview
  ) {
    return isSubscription ? <SubscriptionFlow /> : <SinglePaymentService />;
  } else if (
    role === apiEnums.UserRoleEnum.Par ||
    role === apiEnums.UserRoleEnum.Dnr
  ) {
    return <MatchingInProgress role={role} />;
  }

  return null;
};

const initialValue: AppProtectedContextValue = {
  profile: undefined,
  matchProfileCompleted: false,
  screeningConditionalCompleted: false,
};

export const AppProtectedContext = createContext(initialValue);

export const AppProtectedContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const apiClient = useApiClient();
  const { data: userData } = useUsersMeRetrieve();

  const { data: billingCustomer } = useQuery(
    queryKeys.billingCustomerRetrieve,
    () => apiClient.api.BillingApi.billingCustomerRetrieve(),
    {
      enabled:
        userData?.data?.role === apiEnums.UserRoleEnum.Par ||
        userData?.data?.role === apiEnums.UserRoleEnum.Nap,
    },
  );

  const matchProfileCompleted =
    userData?.data?.profile?.is_match_profile_completed || false;

  const screeningConditionalCompleted =
    userData?.data?.half_passed_screens || false;

  const { role, email, profile } = userData?.data ?? {};
  const { first_name, last_name } = profile || {};

  useEffect(() => {
    if (!email || !settings.getPostHogEnabled()) return;

    posthog.identify(email, {
      name: `${first_name} ${last_name}`,
      role,
    });
  }, [role, email, first_name, last_name]);

  return (
    <AppProtectedContext.Provider
      value={{
        profile: userData?.data,
        matchProfileCompleted,
        screeningConditionalCompleted,
        billingCustomer: billingCustomer?.data,
      }}
    >
      {children}
    </AppProtectedContext.Provider>
  );
};

export const useAppProtected = () => {
  const {
    profile,
    matchProfileCompleted,
    screeningConditionalCompleted,
    billingCustomer,
  } = useContext(AppProtectedContext);

  return {
    profile,
    matchProfileCompleted,
    screeningConditionalCompleted,
    billingCustomer,
  };
};

export const AppProtectedConsumer: FC = () => {
  const { profile, billingCustomer } = useAppProtected();
  const scrollToTopRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    role: myRole,
    status: myStatus,
    passed_screens: hasCompletedScreening,
  } = profile || {};

  const forcedRedirectPath = useForcedRedirect({
    status: myStatus,
    role: myRole,
    hasCompletedScreening,
  });

  useEffect(() => {
    scrollToTopRef?.current?.scrollIntoView();
  }, [pathname]);

  useEffect(() => {
    if (forcedRedirectPath) {
      navigate(forcedRedirectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedRedirectPath]);

  return (
    <AppShell
      ref={scrollToTopRef}
      top={<TopNav profileData={profile} />}
      center={
        myRole && myStatus ? (
          <ConfirmationDialogProvider>
            <ModalProvider>
              <Routes>
                <Route path="screening/*" element={<Screening />} />
                <Route path="matching/*" element={<Matching />} />
                <Route
                  path={`${appPaths.navigator}/*`}
                  element={<Navigator />}
                />
                {myRole === apiEnums.UserRoleEnum.Par &&
                  settings.getIpDashboardFeatureEnabled() && (
                    <Route
                      path={`${appPaths.dashboard}/*`}
                      element={<Dashboard />}
                    />
                  )}
                <Route
                  path="profile"
                  element={<ContactDetails role={myRole} />}
                />
                <Route
                  path={appPaths.uploadRecords}
                  element={<UploadRecords />}
                />
                <Route
                  path={appPaths.careGapRecommendations}
                  element={<CareGapRecommendations />}
                />
                <Route
                  path={`${appPaths.paymentService}/*`}
                  element={
                    <PaymentServiceElement
                      role={myRole}
                      status={myStatus}
                      isSubscription={!!billingCustomer?.is_subscription_user}
                      isMatchProfileCompleted={
                        profile?.profile?.is_match_profile_completed
                      }
                      matchProfileReviewStatus={
                        profile?.profile?.match_profile_review_status
                      }
                    />
                  }
                />
              </Routes>
            </ModalProvider>
          </ConfirmationDialogProvider>
        ) : (
          <LoadingScreen />
        )
      }
    />
  );
};

export const AppProtected = () => {
  return (
    <AppProtectedContextProvider>
      <AppProtectedConsumer />
    </AppProtectedContextProvider>
  );
};
